import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";
import { pushSupported, enablePush, disablePush } from "@/lib/push";

const CATS = [
  ["liveReminders", "Live program reminders"],
  ["newEvents", "New events"],
  ["newVod", "New videos (VOD)"],
  ["highlights", "New goal clips & highlights"],
  ["goalAlerts", "Rwanda goals, kick-off & full time"],
  ["promotions", "Promotions & offers"],
];
const CHANS = [
  ["sms", "SMS"],
  ["whatsapp", "WhatsApp"],
  ["email", "Email"],
  ["push", "Push"],
];
const MUTE_OPTIONS = [
  ["off", "Not muted"],
  [1, "1 day"],
  [7, "7 days"],
  [30, "30 days"],
  ["forever", "Until I turn them back on"],
];

const Toggle = ({ on, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} role="switch" aria-checked={on}
    className={`relative w-11 h-6 rounded-full transition-colors ${on ? "bg-[#E11D2A]" : "bg-[#273042]"}`}>
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${on ? "translate-x-5" : ""}`} />
  </button>
);

export const NotificationSettingsCard = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [hasPush, setHasPush] = useState(false);

  useEffect(() => {
    endpoints.myNotificationSettings().then((r) => { setS(r.data); setHasPush(r.data.hasPush); }).catch(() => {});
  }, []);

  const persist = async (next) => {
    setSaving(true);
    try { const r = await endpoints.updateMyNotificationSettings(next); setS((p) => ({ ...p, ...r.data })); }
    catch { toast.error("Could not save settings"); } finally { setSaving(false); }
  };

  const togglePref = (key) => {
    const prefs = { ...s.prefs, [key]: !s.prefs[key] };
    setS({ ...s, prefs });
    persist({ prefs });
  };

  const setMute = (val) => persist({ muteDays: val === "off" ? null : val });

  const currentMute = s?.muteUntil === "forever" ? "forever" : s?.muted ? "custom" : "off";

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (hasPush) { await disablePush(); setHasPush(false); toast.success("Push disabled on this device"); }
      else { await enablePush(); setHasPush(true); toast.success("Push enabled on this device"); }
    } catch (e) { toast.error(e.message || "Push not available"); } finally { setPushBusy(false); }
  };

  if (!s) return null;

  return (
    <div className="mt-6 rounded-2xl border border-[#273042] bg-[#121620] p-5" data-testid="notification-settings-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-white font-bold"><Bell className="w-5 h-5 text-[#E11D2A]" /> Notification settings</div>
        {saving && <Loader2 className="w-4 h-4 animate-spin text-slate-500" />}
      </div>
      <p className="text-xs text-slate-500 mt-1">Choose what you hear about. Security, OTP and payment messages are always sent.</p>

      <div className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">What</div>
          {CATS.map(([k, label]) => (
            <div key={k} className="flex items-center justify-between py-1.5">
              <span className="text-sm text-slate-200">{label}</span>
              <Toggle on={!!s.prefs[k]} onClick={() => togglePref(k)} testid={`pref-${k}`} />
            </div>
          ))}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">How</div>
          {CHANS.map(([k, label]) => (
            <div key={k} className="flex items-center justify-between py-1.5">
              <span className="text-sm text-slate-200">{label}</span>
              <Toggle on={!!s.prefs[k]} onClick={() => togglePref(k)} testid={`chan-${k}`} />
            </div>
          ))}
        </div>
      </div>

      {s.programs?.length > 0 && (
        <div className="mt-4" data-testid="program-prefs">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Programs — reminders before they start</div>
          <div className="grid sm:grid-cols-2 gap-x-8">
            {s.programs.map((p) => (
              <div key={p.name} className="flex items-center justify-between py-1.5">
                <span className="text-sm text-slate-200 truncate pr-3">{p.name}</span>
                <Toggle on={p.on && !!s.prefs.liveReminders} testid={`program-pref-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => { const programs = s.programs.map((x) => (x.name === p.name ? { ...x, on: !x.on } : x)); setS({ ...s, programs }); persist({ programs: { [p.name]: !p.on } }); }} />
              </div>))}
          </div>
          {!s.prefs.liveReminders && <p className="text-[11px] text-slate-500 mt-1">Turn on live reminders above to get program alerts.</p>}
        </div>
      )}

      {pushSupported() && (
        <div className="mt-4 flex items-center justify-between rounded-xl border border-[#273042] bg-[#0A0C10] p-3">
          <div className="flex items-center gap-2 text-sm text-slate-200"><Smartphone className="w-4 h-4 text-[#2563EB]" /> Push on this device</div>
          <button onClick={togglePush} disabled={pushBusy} data-testid="push-toggle-btn"
            className={`h-9 px-4 rounded-full text-sm font-bold flex items-center gap-1.5 ${hasPush ? "bg-white/5 border border-[#273042] text-slate-300" : "bg-[#2563EB] text-white"}`}>
            {pushBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {hasPush ? "Disable" : "Enable"}
          </button>
        </div>
      )}

      <div className="mt-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1"><BellOff className="w-3.5 h-3.5" /> Mute all notifications</div>
        <div className="flex flex-wrap gap-2">
          {MUTE_OPTIONS.map(([val, label]) => {
            const active = (val === "off" && currentMute === "off") || (val === "forever" && currentMute === "forever");
            return (
              <button key={String(val)} onClick={() => setMute(val)} data-testid={`mute-${val}`}
                className={`h-9 px-3 rounded-full text-xs font-semibold border transition-colors ${active ? "border-[#E11D2A] bg-[#E11D2A]/10 text-white" : "border-[#273042] bg-[#0A0C10] text-slate-300"}`}>
                {label}
              </button>
            );
          })}
        </div>
        {s.muted && <p className="text-[11px] text-amber-400 mt-2">Notifications are muted{s.muteUntil && s.muteUntil !== "forever" ? ` until ${new Date(s.muteUntil).toLocaleString()}` : ""}.</p>}
      </div>
    </div>
  );
};
